/**
 * A project's pictures (the workbench's diagrams kept at the project's home): one card each, with
 * its look, version and who saved it. Opening one shows the picture, the version before it when
 * there is one, and a download. The SVG is drawn as an image, so nothing in it can run; the
 * platform has also refused scripts, handlers and outside links before keeping it.
 */

import type { PictureBy, ProjectPicture, ProjectPicturePrevious, ProjectPictureSummary } from '@aioe/shared';
import { Picture } from '@gravity-ui/icons';
import { useEffect, useMemo, useState } from 'react';

import { routes } from '../api/client.ts';
import { useAgentName } from '../agentNames.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { Button, Empty, ErrorNote, ListBlock, ListGroup, ListRow } from './ui.tsx';

const tone = 'violet' as const;

/** An SVG as an image address, released when the picture closes. */
function useSvgUrl(svg: string | undefined): string | undefined {
  const url = useMemo(() => (svg ? URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' })) : undefined), [svg]);
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);
  return url;
}

function SvgImage({ svg, title }: { svg: string; title: string }) {
  const url = useSvgUrl(svg);
  return url ? <img src={url} alt={title} className="mx-auto max-h-[70vh] w-full rounded-lg bg-white object-contain p-2" /> : null;
}

export function ProjectPictures({ projectId }: { projectId: string }) {
  const list = useLoad(() => routes.projectPictures(projectId), [projectId], 60_000);
  const [open, setOpen] = useState<string>();
  const pictures = list.data?.pictures ?? [];
  const people = list.data?.people ?? {};
  const agentName = useAgentName();
  const who = (b: PictureBy) => [b.principal ? (people[b.principal]?.name ?? b.principal) : undefined, agentName(b.actor)].filter(Boolean).join(' with ') || 'someone';
  if (list.data && pictures.length === 0) return null;
  return (
    <ListGroup title="Pictures" count={pictures.length || undefined} description="Diagrams the team keeps for the project, drawn in AI Workbench. Open one to see it." empty={list.data && pictures.length === 0 ? <Empty tone={tone} icon={<Picture />} compact title="No pictures yet" body="A diagram saved to the project in AI Workbench shows here." /> : undefined}>
      {list.error ? (
        <ListBlock>
          <ErrorNote error={list.error} />
        </ListBlock>
      ) : null}
      {pictures.flatMap((p) => [
        <ListRow
          key={p.slug}
          icon={<Picture />}
          title={p.title}
          description={`${p.kind} · ${p.look} · version ${p.version} · ${p.editedAt ? 'edited' : 'saved'} by ${who(p.editedAt && p.editedBy ? p.editedBy : p.savedBy)} ${timeAgo(p.savedAt)}`}
          trailing={open === p.slug ? <span className="text-xs text-muted">Open</span> : undefined}
          onPress={() => setOpen(open === p.slug ? undefined : p.slug)}
        />,
        open === p.slug ? <PictureView key={`${p.slug}/open`} projectId={projectId} summary={p} who={who} /> : null,
      ])}
    </ListGroup>
  );
}

function PictureView({ projectId, summary, who }: { projectId: string; summary: ProjectPictureSummary; who: (b: PictureBy) => string }) {
  const [shown, setShown] = useState<'current' | 'previous'>('current');
  const loaded = useLoad<ProjectPicture | ProjectPicturePrevious>(() => (shown === 'current' ? routes.projectPicture(projectId, summary.slug) : routes.projectPicturePrevious(projectId, summary.slug)), [projectId, summary.slug, summary.version, shown]);
  const picture = loaded.data;
  const error = loaded.error;
  const download = () => {
    if (!picture) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([picture.svg], { type: 'image/svg+xml' }));
    a.download = `${summary.slug}${shown === 'previous' ? `-v${picture.version}` : ''}.svg`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 text-sm">
          <span className="font-medium">{summary.title}</span>
          <span className="text-muted">{picture ? ` · version ${picture.version}, saved by ${who(picture.savedBy)} ${timeAgo(picture.savedAt)}` : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          {summary.previous ? (
            <Button size="sm" variant="ghost" onPress={() => setShown(shown === 'current' ? 'previous' : 'current')}>
              {shown === 'current' ? `Version ${summary.previous.version}, before` : 'The current version'}
            </Button>
          ) : null}
          <Button size="sm" variant="secondary" isDisabled={!picture} onPress={download}>
            Download SVG
          </Button>
        </div>
      </div>
      <ErrorNote error={error} />
      {picture ? <SvgImage svg={picture.svg} title={summary.title} /> : !error ? <p className="text-sm text-muted">Opening the picture…</p> : null}
    </div>
  );
}
